
import { Component, Vue, Prop } from 'vue-property-decorator';
import { axios } from '@/helpers/axios';
import { AxiosResponse } from 'axios';
import { colors } from '@/assets/colors/colors';
import ChartHeading from '@/components/DataTab/Charts/ChartComponents/ChartHeading.vue';
import ChartComponent from '@/components/DataTab/Charts/ChartComponents/ChartComponent.vue';
import { AvgIwrDataType, ChartLoadingStatus, PlotlyTraceType } from '@/types';

type CategoryType =
  | 'iwrcf_east_hot'
  | 'iwrcf_east_avg'
  | 'iwrcf_west_hot'
  | 'iwrcf_west_avg';

@Component({
  components: {
    ChartHeading,
    ChartComponent,
  },
})
export default class Plot26 extends Vue {
  @Prop() readonly areaId!: number;

  chartId = 26;
  chartData: AvgIwrDataType[] = [];
  dataStatus: ChartLoadingStatus = 'idle';
  categoryList: CategoryType[] = [
    'iwrcf_east_hot',
    'iwrcf_east_avg',
    'iwrcf_west_hot',
    'iwrcf_west_avg',
  ];

  iwrColorMap: Record<CategoryType, string> = {
    iwrcf_east_avg: colors.darkestBlue,
    iwrcf_west_hot: colors.mediumBlue,
    iwrcf_west_avg: colors.teal,
    iwrcf_east_hot: colors.purple,
  };

  mounted() {
    // fetch data
    this.dataStatus = 'loading';
    axios
      .get(`/charts/${this.chartId}/${this.areaId}`)
      .then((response: AxiosResponse) => {
        this.chartData = response.data.data;
        this.dataStatus = 'success';
      })
      .catch(() => (this.dataStatus = 'error'));
  }

  get unit() {
    return this.$t(`data.chartTables.${this.chartId}.unit`);
  }

  get plotlyData() {
    const formattedData: PlotlyTraceType[] = [];
    const localeTimeUnit = this.$t(`data.chartTables.timeUnits.year`);
    this.categoryList.forEach(cat => {
      const catName = this.$t(
        `data.chartTables.${this.chartId}.categories.${cat}`,
      );
      const trace: PlotlyTraceType = {
        x: [],
        y: [],
        name: catName,
        type: 'scatter',
        mode: 'lines+markers',
        marker: {
          color: this.iwrColorMap[cat],
          size: 8,
          symbol: 'circle-open',
        },
        hovertemplate: `<b>${localeTimeUnit}: </b>%{x}<br><b>${catName}:</b> %{y:,.2f} ${this.unit}<extra></extra>`,
      };

      this.chartData.forEach(item => {
        trace.y.push(item[cat]);
        trace.x.push(item.year);
      });
      formattedData.push(trace);
    });
    return formattedData;
  }

  get layout() {
    const label = this.$t(`data.chartTables.${this.chartId}.axisLabels.y`);
    const unit = this.$t(`data.chartTables.${this.chartId}.unit`);
    return {
      yaxis: {
        fixedrange: true,
        automargin: true,
        title: {
          text: `${label} (${unit})`,
          standoff: 10,
        },
      },
      xaxis: {
        fixedrange: true,
      },
      legend: { orientation: 'h', y: -0.2 },
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      hovermode: 'closest',
      // removes empty heading above graph
      margin: {
        l: 50,
        r: 50,
        b: 50,
        t: 5,
        pad: 3,
      },
    };
  }
}
